import React, { FC, useCallback, useEffect } from 'react';
import { Button, Form, FormItem, Header, InfoBlock, Input, ScrollView } from '$uikit';
import { formatInputAmount } from '$utils/number';
import { AttachPhoto, CurrencyEnum } from '$store/models';
import { IcChevron16, IcHelp28 } from '$assets';
import { useModalClose, useTranslator, useWikiLink } from '$hooks';
import { confirmSheet, formatCurrency, statReachGoal } from 'utils';
import { BundleAttach } from '$core/CreateBundle/BundleAttach/BundleAttach';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '$store/rootReducer';
import { attachPhotosSelector } from '$store/attach';
import { bundlesActions, bundleSelector, bundlesFormSelector } from '$store/bundles';
import { CreateBundleProps } from '$core/CreateBundle/CreateBundle.interface';
import { toastActions } from '$store/toast';
import { Loader, Tooltip } from '$shared/components';
import { goBack } from '$navigation/helper';
import { mainSelector } from '$store/main';

const MIN_PRICE = 0;
const MAX_PRICE = 500;
const MAX_PRICE_WITHOUT_DISCOUNT = 400;
const MAX_PRICE_WITH_DISCOUNT = 400;

export const CreateBundle: FC<CreateBundleProps> = ({ editId }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { isBlackFriday2025Enabled, priceLimits } = useSelector(mainSelector);
  const customMaxPrice = priceLimits.bundle;
  const maxPrice = customMaxPrice ?? MAX_PRICE;
  const maxPriceWithoutDiscount = customMaxPrice ?? MAX_PRICE_WITHOUT_DISCOUNT;
  const maxPriceWithDiscount = customMaxPrice ?? MAX_PRICE_WITH_DISCOUNT;

  const form = useSelector(bundlesFormSelector);
  const bundle = useSelector((root: RootState) => bundleSelector(root, editId || 0));
  const helpLink = useWikiLink('/for-creators/bundles');
  const decryptKeys = useSelector((state: RootState) => state.bundles.decryptKeys);
  const decryptKey = editId ? decryptKeys[editId] : undefined;

  const mediaPreviewAttachments: AttachPhoto[] = useSelector((state: RootState) =>
    attachPhotosSelector(state, 'bundle_previews'),
  );
  const mediaContentAttachments: AttachPhoto[] = useSelector((state: RootState) =>
    attachPhotosSelector(state, 'bundle_content'),
  );

  const handleClose = () => {
    if (
      form.price ||
      form.name ||
      form.description ||
      mediaPreviewAttachments.length ||
      mediaContentAttachments.length
    ) {
      confirmSheet(t('form_reset_confirm')).then((value) => {
        if (value) {
          goBack();
          dispatch(bundlesActions.clearForm());
        }
      });
      return false;
    } else {
      goBack();
      dispatch(bundlesActions.clearForm());
      return false;
    }
  };

  useModalClose(editId ? 'edit_bundle' : 'create_bundle', () => {
    return handleClose();
  });

  useEffect(() => {
    editId && !form.id && dispatch(bundlesActions.editBundle(editId));
  }, [editId]);

  const handleSave = useCallback(() => {
    if (!form.price || parseFloat(form.price) < 1) {
      return dispatch(toastActions.setToastFail(t('bundles_create_toast_price')));
    } else if (!form.name.length) {
      return dispatch(toastActions.setToastFail(t('bundles_create_toast_name_required')));
    } else if (!form.description.length) {
      return dispatch(toastActions.setToastFail(t('bundles_create_toast_desc_required')));
    } else if (!mediaPreviewAttachments.length) {
      return dispatch(
        toastActions.setToastFail(t('bundles_create_toast_preview_required')),
      );
    } else if (!mediaContentAttachments.length) {
      return dispatch(
        toastActions.setToastFail(t('bundles_create_toast_content_required')),
      );
    }
    if (editId) {
      dispatch(bundlesActions.saveBundle());
      statReachGoal('bundles_bundle_edit');
    } else {
      dispatch(bundlesActions.createBundle());
      statReachGoal('bundles_bundle_create');
    }
  }, [editId, form, mediaPreviewAttachments, mediaContentAttachments, t]);

  const formPrice = Math.max(parseFloat(form.price || '0'), 0);
  const formPriceWithoutDiscount = Math.max(
    formPrice,
    parseFloat(form.priceWithoutDiscount || '0'),
  );
  const savings = formPriceWithoutDiscount - formPrice;

  return (
    <>
      <Header
        showCloseButton
        backButtonOnClick={handleClose}
        rightIcons={[
          {
            icon: (
              <Tooltip
                iconShown={false}
                title={t('bundles_form_tooltip_title')}
                text={t('bundles_form_tooltip_text')}
                action={
                  <Button
                    stretched
                    iconRight={<IcChevron16 />}
                    href={helpLink}
                    target="_blank"
                  >
                    {t('bundles_form_tooltip_button')}
                  </Button>
                }
              >
                <IcHelp28 />
              </Tooltip>
            ),
          },
        ]}
      >
        {editId ? t('bundles_edit_title') : t('bundles_create_title')}
      </Header>
      {!(editId && !form.id) ? (
        <ScrollView
          footer={
            <Button onClick={handleSave}>
              {editId ? t('bundles_save_button') : t('bundles_create_button')}
            </Button>
          }
        >
          <Form>
            <FormItem>
              <InfoBlock type="tip">{t('bundles_form_info')}</InfoBlock>
            </FormItem>

            {isBlackFriday2025Enabled ? (
              <>
                <FormItem
                  title={t('bundles_form_price_without_discount_title')}
                  description={t('bundles_form_price_without_discount_desc')}
                >
                  <Input
                    type="price"
                    onChange={(e) => {
                      dispatch(
                        bundlesActions.setFromPriceWithoutDiscount(
                          formatInputAmount(e.target.value),
                        ),
                      );
                    }}
                    currency={CurrencyEnum.EUR}
                    placeholder="0"
                    value={form.priceWithoutDiscount}
                    minPrice={MIN_PRICE}
                    maxPrice={maxPriceWithoutDiscount}
                  />
                </FormItem>
                <FormItem
                  title={t('bundles_form_price_with_discount_title')}
                  description={t('bundles_form_price_with_discount_desc', {
                    amount: formatCurrency(formPrice * 0.8, CurrencyEnum.EUR),
                    cashback: formatCurrency(
                      Math.min(savings, formPrice * 0.2),
                      CurrencyEnum.EUR,
                    ),
                    recommended: formatCurrency(
                      formPriceWithoutDiscount * 0.7,
                      CurrencyEnum.EUR,
                    ),
                  })}
                >
                  <Input
                    type="price"
                    onChange={(e) => {
                      const raw = formatInputAmount(e.target.value);

                      const priceV = parseFloat(raw || '0');
                      const priceWithoutDiscountV = parseFloat(
                        form.priceWithoutDiscount || '0',
                      );

                      dispatch(bundlesActions.setFromPrice(raw));
                      if (priceV > priceWithoutDiscountV) {
                        dispatch(bundlesActions.setFromPriceWithoutDiscount(raw));
                      }
                    }}
                    currency={CurrencyEnum.EUR}
                    placeholder="0"
                    value={form.price}
                    minPrice={MIN_PRICE}
                    maxPrice={maxPriceWithDiscount}
                  />
                </FormItem>
              </>
            ) : (
              <FormItem
                title={t('bundles_form_price_title')}
                description={t('bundles_form_price_desc', {
                  amount: formatCurrency(formPrice * 0.8, CurrencyEnum.EUR),
                })}
              >
                <Input
                  type="price"
                  onChange={(e) => {
                    dispatch(
                      bundlesActions.setFromPrice(formatInputAmount(e.target.value)),
                    );
                  }}
                  currency={CurrencyEnum.EUR}
                  placeholder="0"
                  value={form.price}
                  minPrice={MIN_PRICE}
                  maxPrice={maxPrice}
                />
              </FormItem>
            )}

            <FormItem
              description={`${form.name.length}/30`}
              title={t('bundles_form_name_title')}
              descriptionAlign="right"
            >
              <Input
                onChange={(e) => {
                  dispatch(bundlesActions.setFromName(e.target.value?.substring(0, 30)));
                }}
                placeholder={t('bundles_form_name_placeholder')}
                value={form.name}
              />
            </FormItem>
            <FormItem
              title={t('bundles_form_description_title')}
              description={t('bundles_form_description_desc')}
            >
              <Input
                onChange={(e) => {
                  dispatch(bundlesActions.setFromDescription(e.target.value));
                }}
                value={form.description}
                multiline
                placeholder={t('bundles_form_description_placeholder')}
              />
            </FormItem>
            <FormItem
              title={t('bundles_form_preview_title')}
              titleAfter={`${mediaPreviewAttachments.length}/10`}
              descriptionBeforeContent
              description={t('bundles_form_preview_desc')}
            >
              <BundleAttach
                showCoverLabel
                pickerId="bundle_previews"
                decryptKey={decryptKey}
                limit={10}
              />
            </FormItem>
            <FormItem
              title={t('bundles_form_content_title')}
              titleAfter={`${mediaContentAttachments.length}/50`}
              descriptionBeforeContent
            >
              <BundleAttach
                pickerId="bundle_content"
                decryptKey={decryptKey}
                limit={50}
              />
            </FormItem>
          </Form>
        </ScrollView>
      ) : (
        <Loader isWrapped />
      )}
    </>
  );
};
